import { useEffect } from "react";

/** Pause CSS `scroll-behavior: smooth` on <html> (set by the revamp shell). */
function withoutSmoothScroll(fn) {
  const html = document.documentElement;
  const prev = html.style.scrollBehavior;
  html.style.scrollBehavior = "auto";
  try {
    fn();
  } finally {
    requestAnimationFrame(() => {
      html.style.scrollBehavior = prev;
    });
  }
}

/** Show scroll-reveal blocks immediately so they do not animate after a jump. */
function settleReveals(root) {
  if (!root || !root.querySelectorAll) return;
  const nodes = [root, ...root.querySelectorAll(".hiw-reveal")];
  nodes.forEach((n) => {
    if (!n.classList || !n.classList.contains("hiw-reveal")) return;
    n.style.transition = "none";
    n.classList.add("hiw-in");
  });
}

/** Scroll to an in-page id. `hash` may be "section" or "#section". */
export function scrollToSection(hash) {
  const id = String(hash || "").replace(/^#/, "");
  if (!id) return false;
  const el = document.getElementById(id);
  if (!el) return false;
  const margin = parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0;
  const top = Math.max(0, window.scrollY + el.getBoundingClientRect().top - margin);
  withoutSmoothScroll(() => {
    window.scrollTo(0, top);
  });
  settleReveals(el);
  return true;
}

/**
 * Same-page hash clicks do not reliably re-scroll after the first one
 * (URL already has a hash; React Router also skips a no-op location).
 * Call this from Link / raw anchors. Cross-page hashes are left alone.
 */
export function onSectionNavClick(event, href) {
  if (!event || event.defaultPrevented) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
  const raw = String(href || "");
  const i = raw.indexOf("#");
  if (i < 0 || raw.length < i + 2) return;
  const path = raw.slice(0, i);
  const hash = raw.slice(i);
  if (path && path !== window.location.pathname) return;
  event.preventDefault();
  if (!scrollToSection(hash)) return;
  if (window.history.replaceState) {
    window.history.replaceState(null, "", window.location.pathname + window.location.search + hash);
  }
}

/** Land on /page#section when arriving from another page. */
export function useHashScrollOnMount() {
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return undefined;
    const t = setTimeout(() => scrollToSection(hash), 150);
    return () => clearTimeout(t);
  }, []);
}
